import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MEMBERSHIP_URL } from "@/lib/subscriptionAccess";

/**
 * LockedOverlay - Consistent locked state UI for gated features
 * 
 * Usage:
 * <div className="relative">
 *   <YourComponent />
 *   {!hasAccess && <LockedOverlay requiredTier="quarterly" isFreeUser={true} />}
 * </div>
 */
export function LockedOverlay({ 
  requiredTier = "quarterly",
  message = null,
  ctaText = null,
  ctaLink = MEMBERSHIP_URL,
  isFreeUser = false,
  className = ""
}) {
  // Default messages based on tier and user status
  const defaultMessages = {
    monthly: "Subscribe to unlock",
    quarterly: "Quarterly plan or above required",
    annual: "Annual plan required"
  };
  
  const defaultCTAText = {
    monthly: "Subscribe Now",
    quarterly: "Upgrade to Quarterly",
    annual: "Upgrade to Annual"
  };

  // Free user always sees "Upgrade to unlock" with consistent CTA
  const displayMessage = isFreeUser 
    ? (message || "Upgrade to unlock")
    : (message || defaultMessages[requiredTier] || "Upgrade to unlock");
    
  const displayCTA = isFreeUser
    ? (ctaText || "Upgrade")
    : (ctaText || defaultCTAText[requiredTier] || "Upgrade your plan");

  return (
    <div 
      className={`absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl backdrop-blur-sm ${className}`}
      style={{ backgroundColor: 'rgba(45, 74, 68, 0.85)', pointerEvents: 'none' }}
      data-testid="locked-overlay"
    >
      <div className="flex flex-col items-center gap-3 p-4 text-center" style={{ pointerEvents: 'auto' }}>
        <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
          <Lock className="w-6 h-6 text-[#a69373]" />
        </div>
        <p className="text-white/80 text-sm font-medium max-w-[200px]">
          {displayMessage}
        </p>
        <Button
          className="bg-[#a69373] hover:bg-[#d4b47a] text-[#0B2821] text-sm font-semibold px-4 py-2"
          style={{ pointerEvents: 'auto', touchAction: 'manipulation' }}
          onClick={() => window.open(ctaLink, '_blank', 'noopener,noreferrer')}
        >
          {displayCTA}
        </Button>
      </div>
    </div>
  );
}

/**
 * LockedBadge - Small lock indicator for badge items
 */
export function LockedBadge({ tooltip = "Available on Quarterly and above" }) {
  return (
    <div 
      className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-black/60"
      style={{ pointerEvents: 'none' }}
      title={tooltip}
    >
      <Lock className="w-4 h-4 text-white/50" />
    </div>
  );
}

/**
 * LockedCard - Wrapper component that adds lock overlay to children
 */
export function LockedCard({ 
  isLocked = false, 
  requiredTier = "quarterly",
  message = null,
  ctaText = null,
  children,
  className = ""
}) {
  return (
    <div className={`relative ${className}`}>
      {children}
      {isLocked && (
        <LockedOverlay 
          requiredTier={requiredTier}
          message={message}
          ctaText={ctaText}
        />
      )}
    </div>
  );
}

export default LockedOverlay;
